import { Avatar, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useIsDesktop } from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import { notDigits } from 'Regex';
import { ChangeEvent } from 'react';

interface IInput {
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  error: string;
  label: string;
}

interface IControlledPhoneInput {
  legend?: string;
  prefixes: { id: number; value: string }[];
  prefix: IInput & {
    noPrefixOptionLabel: string;
    prefixValue?: number;
  };
  number: IInput & {
    numberValue?: string;
  };
  onBlur?: React.FocusEventHandler<HTMLFieldSetElement>;
}

export const ControlledPhoneInput = ({
  legend,
  prefixes,
  prefix: {
    prefixValue,
    handleChange: handlePrefixChange,
    error: prefixError,
    label: prefixLabel,
    noPrefixOptionLabel,
  },
  number: {
    label: numberLabel,
    numberValue,
    handleChange: handleNumberChange,
    error: numberError,
  },
  onBlur,
}: IControlledPhoneInput) => {
  const isDesktop = useIsDesktop();

  return (
    <Grid
      onBlur={onBlur}
      container
      item
      component='fieldset'
      sx={{
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        p: '0',
        borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': {
          bgcolor: 'white',
          zIndex: -1,
          borderRadius: '6px',
          borderColor: 'primary.shade_200',
        },
        '&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderRadius: '6px',
          borderWidth: '1px',
          borderColor: 'primary.main',
        },
      }}
    >
      {legend && <legend>{legend}</legend>}

      <TextField
        id='select-phone-prefix'
        select
        value={prefixValue}
        variant='outlined'
        onChange={handlePrefixChange}
        error={!!prefixError}
        helperText={prefixError}
        label={prefixLabel}
        sx={{
          width: '108px',
          ml: '8px',
        }}
        SelectProps={{
          MenuProps: {
            sx: {
              maxHeight: '244px',
              boxShadow: '0px 6px 21px rgba(45, 112, 217, 0.15)',
            },
            MenuListProps: {
              sx: {
                '& :hover': { bgcolor: 'primary.shade_100' },
              },
            },
            PaperProps: { sx: { borderRadius: '6px' } },
          },
          IconComponent: (props) => {
            return (
              <Avatar
                {...props}
                alt=''
                src={AccordionArrowPurple}
                sx={{
                  width: '32px',
                  height: '32px',
                  position: 'absolute',
                  right: '7px',
                  mt: '-7px',
                }}
              />
            );
          },
        }}
        InputProps={{ sx: { minHeight: '48px', height: '48px', mt: '5px' } }}
      >
        {noPrefixOptionLabel && (
          <MenuItem key={'empty'} value={''}>
            {noPrefixOptionLabel}
          </MenuItem>
        )}
        {prefixes.map((prefix) => (
          <MenuItem key={prefix.id} value={prefix.id}>
            <Typography variant={'body3'}>{prefix.value}</Typography>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        autoComplete='off'
        id='input-phone-number'
        label={numberLabel}
        value={numberValue}
        onChange={handleNumberChange}
        onInput={(e: any) => {
          const newValue = e.target.value.replace(notDigits, '');
          e.target.value = newValue;
        }}
        type='tel'
        inputProps={{
          maxLength: 7,
        }}
        sx={{
          mr: '5px',
          width: isDesktop ? '282px' : '63%',
        }}
        error={!!numberError}
        helperText={numberError}
        InputProps={{ sx: { minHeight: '53px', height: '53px' } }}
      />
    </Grid>
  );
};
